import React, { useState, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { v4 } from 'uuid';
import { Status } from 'apis/Status';
import {
  updateStatus,
  updateSinglePageDisplayLiveLogs,
  updateSinglePageSelectedEventLogIndex,
  setDropModalOpen,
  updateSinglePageLocalLogList,
  doUpdateHistoricalEventLogs,
  storeHistoricalEventLogs
} from 'redux/actions';
import { PerfectScrollbar } from 'components/shared/ReactPerfectScrollbar';
import { IconAndText } from 'components/shared/SvgAF/SvgAF';
import SvgAF from 'components/shared/SvgAF/SvgAF';
import LogsTable from '../LogsTable/LogsTable';
import TextFileDownloader from 'components/shared/TextFileDownloader/TextFileDownloader';
import { IconLoader } from 'common/IconLoader';
import Modal from 'components/shared/Modal/Modal';
import DropModal from 'components/shared/DropModal/DropModal';
import classnames from 'classnames';
import { isEventLogTruncated } from './utils';

import './ViewLogModal.css';

/**
 * a popup modal that let users to view single device related data and create snapshot
 */
const ViewLogModal = ({ deviceId, deviceConnectionState, onClose }) => {
  const [titleId] = useState(() => v4());
  const [retrieveEventLogErrorCode, setRetrieveEventLogErrorCode] = useState();

  const historicalStatusName = deviceId && `fetchHistoricalEventLogs-${deviceId}`;
  const liveStatusName = deviceId && `fetchLiveEventLogs-${deviceId}`;
  const createSnapshotTimeoutStatusName = deviceId && `createSnapshotTimeout-${deviceId}`;

  // request statuses
  const historicalFetching = useSelector(
    (store) => historicalStatusName && store.statuses[historicalStatusName] === Status.PENDING
  );
  const historicalError = useSelector(
    (store) => historicalStatusName && store.statuses[historicalStatusName] === Status.FAILURE
  );

  // logs data
  const historicalEventLogs = useSelector((store) => store.data?.historicalEventLogs?.[deviceId]);
  const allHistoricalEventLogs = useSelector((store) => store.data.historicalEventLogs);

  // live mode (future)
  const liveFetching = useSelector((store) => liveStatusName && store.statuses[liveStatusName] === Status.PENDING);
  const liveError = useSelector((store) => liveStatusName && store.statuses[liveStatusName] === Status.FAILURE);
  const liveEventLogs = useSelector(
    (store) => deviceId && store.data.liveEventLogs && store.data.liveEventLogs[deviceId]
  );

  const selectedEventLogIndex = useSelector(
    (store) => deviceId && store.ui.singleDevicePage.selectedEventLogIndexObj[deviceId]
  );
  const selectedEventLogIndexObj = useSelector((store) => store.ui.singleDevicePage.selectedEventLogIndexObj);
  const displayLiveLogs = useSelector((store) => store.ui.singleDevicePage.displayLiveLogs);
  const localLogList = useSelector((store) => store.ui.singleDevicePage.localLogList);
  const createSnapshotTimeoutMsg = useSelector((store) =>
    createSnapshotTimeoutStatusName ? store.statuses[createSnapshotTimeoutStatusName] : ''
  );

  const hasCmEventLogsPermission = useSelector((store) =>
    store.data?.rolePermissions?.permissions.some((permission) => permission === 'cm_eventlog')
  );

  const dispatch = useDispatch();

  const handleCreateSnapshot = (deviceId, historicalFetching, historicalEventLogs, selectedEventLogIndex) => {
    dispatch(updateSinglePageDisplayLiveLogs(false));
    dispatch(updateSinglePageSelectedEventLogIndex(deviceId, 0));

    const cb_success = (result) => {
      dispatch(storeHistoricalEventLogs(deviceId, result));
      let events = result?.[0]?.events ?? [];
      dispatch(updateSinglePageLocalLogList(events));
    };

    const cb_failed = (error) => {
      let events = historicalEventLogs?.[selectedEventLogIndex]?.events ?? [];
      dispatch(updateSinglePageLocalLogList(events));
      console.log('doUpdateHistoricalEventLogserror', error);
    };

    const cb_success_retrieval = (result) => {
      // console.log('result from create snapshot', result);
    };

    const cb_failed_retrieval = (error) => {
      // console.log('error from create snapshot', error.message);
      setRetrieveEventLogErrorCode(error.message || '408');
    };

    !historicalFetching &&
      dispatch(
        doUpdateHistoricalEventLogs(
          historicalEventLogs?.[0]?.timestamp,
          deviceId,
          0,
          100,
          'timestamp',
          'desc',
          cb_success,
          cb_failed,
          cb_success_retrieval,
          cb_failed_retrieval
        )
      );
  };

  const timestampOptions = historicalEventLogs
    ? historicalEventLogs.map((logObj) => moment(logObj.timestamp).format('MMM DD, YYYY HH:mm:ss'))
    : [];
  const isCreateSnapshotButtonDisabled = historicalFetching || deviceConnectionState !== 'Connected';

  let deviceEventLogs;
  if (displayLiveLogs) {
    deviceEventLogs = liveEventLogs && liveEventLogs;
  } else {
    deviceEventLogs = historicalEventLogs && historicalEventLogs[selectedEventLogIndex];
  }

  let timestamp = deviceEventLogs && deviceEventLogs.timestamp;
  let timestampString = timestamp && moment(timestamp).format('MMM DD, YYYY HH:mm:ss');
  let list = [];

  const createRowString = (string, minLength) => {
    return string + new Array(minLength - string.length + 1).join(' ');
  };

  if (deviceEventLogs) {
    const columnString = `${createRowString('TIME SINCE THE LAST REBOOT', 33)}${createRowString(
      'TIMESTAMP',
      33
    )}${createRowString('NAME', 36)}${createRowString('CATEGORY', 20)}MESSAGE`;
    list.push(columnString);
    list.push(' ');

    let events = localLogList && localLogList;

    events &&
      events.forEach((log) => {
        const timeSinceBoot = createRowString(log.time_since_boot.toString(), 33);
        const timestamp = createRowString(
          log.calendar_time ? moment(log.calendar_time).format('MMM DD, YYYY HH:mm:ss') : 'N/A',
          33
        );
        const name = createRowString(log.name, 36);
        const category = createRowString(log.category, 20);
        const logString = `${timeSinceBoot}${timestamp}${name}${category}${log.message}`;
        list.push(logString);
      });
  }

  let timestampContainerHeight = 220;
  if (timestampOptions && timestampOptions.length < 5) {
    timestampContainerHeight = 43 * timestampOptions.length;
  }

  let timesstampDropdown = (
    <PerfectScrollbar>
      <div className="viewLogModal_TimestampContainer" style={{ height: `${timestampContainerHeight}px` }}>
        {timestampOptions &&
          timestampOptions.map((timesstampStr, idx) => {
            return (
              <div
                key={idx}
                className="viewLogModal_TimestampContainer_Item_Div"
                onClick={() => {
                  if (historicalError) {
                    const historicalStatusName = deviceId && `fetchHistoricalEventLogs-${deviceId}`;
                    const createSnapshotTimeoutStatusName = deviceId && `createSnapshotTimeout-${deviceId}`;
                    dispatch(updateStatus(historicalStatusName, Status.NONE));
                    dispatch(updateStatus(createSnapshotTimeoutStatusName, Status.NONE));
                  }
                  dispatch(updateSinglePageDisplayLiveLogs(false));
                  dispatch(updateSinglePageSelectedEventLogIndex(deviceId, idx));
                  dispatch(setDropModalOpen('viewHistoricalLogsDropModal', false));
                  let events =
                    historicalEventLogs && historicalEventLogs[idx] && historicalEventLogs[idx].events
                      ? historicalEventLogs[idx].events
                      : [];
                  dispatch(updateSinglePageLocalLogList(events));
                }}
              >
                <div className="viewLogModal_TimestampContainer_Item_Text">{timesstampStr}</div>
              </div>
            );
          })}
      </div>
    </PerfectScrollbar>
  );

  let viewHistoricalLogTitle = (
    <IconAndText
      img={IconLoader.icon_historical_log}
      hoverImg={IconLoader.icon_historical_log_hover}
      text={
        timestampOptions && timestampOptions[selectedEventLogIndex]
          ? `${timestampOptions[selectedEventLogIndex]}`
          : 'View snapshots'
      }
    />
  );

  return (
    <Modal
      aria-labelledby={titleId}
      width={1380}
      height={680}
      noPadding={true}
      colorize={'#f8f9fa'}
      opaque={true}
      global
      close={onClose}
      className={'viewLogModal'}
    >
      <div className="viewLogModal_Wrapper">
        <div className="viewLogModal_Container">
          <div className="viewLogModal_Header">
            <div className="viewLogModal_DeviceBoxContainer">
              <div id={titleId} className="viewLogModal_DeviceBox">
                <div className="viewLogModal_DeviceHeader">
                  <div className="viewLogModal_DeviceTitle">Current device</div>
                </div>
                {deviceId && (
                  <div className="viewLogModal_DeviceDetail">
                    <div className="viewLogModal_DeviceId">{deviceId}</div>
                  </div>
                )}
              </div>
              <div
                className={classnames({
                  viewLogModal_exportButtonContainer: true,
                  disabled_button: historicalFetching || historicalError || timestampOptions?.length === 0
                })}
              >
                <TextFileDownloader
                  list={list}
                  fileName={`eventlog-${deviceId}-${timestampString}`}
                  disabled={historicalFetching || historicalError || timestampOptions?.length === 0 ? true : false}
                />
              </div>
            </div>
            <div className="viewLogModal_TileHeader">
              <div className="viewLogModal_RetrieveLogsButtons">
                <div
                  className={classnames({
                    disabled_button: historicalFetching || timestampOptions?.length === 0
                    // || historicalError
                  })}
                >
                  <DropModal title={viewHistoricalLogTitle} name={'viewHistoricalLogsDropModal'}>
                    {timesstampDropdown}
                  </DropModal>
                </div>
                {hasCmEventLogsPermission && (
                  <div
                    className={classnames({
                      viewHistoricalLogsButton: !isCreateSnapshotButtonDisabled,
                      viewHistoricalLogsButton_Disabled: isCreateSnapshotButtonDisabled
                    })}
                    onClick={() =>
                      !isCreateSnapshotButtonDisabled &&
                      handleCreateSnapshot(deviceId, historicalFetching, historicalEventLogs, selectedEventLogIndex)
                    }
                  >
                    <div
                      className="viewHistoricalLogsButton_Title"
                      title={
                        deviceConnectionState !== 'Connected'
                          ? 'Device must be online to perform the action.'
                          : 'Create snapshot'
                      }
                    >
                      Create snapshot
                    </div>
                  </div>
                )}
                {isEventLogTruncated(historicalEventLogs[selectedEventLogIndex]) && (
                  <div className="viewLogModal_truncatedWarning">
                    <div className="viewLogModal_truncatedWarning_icon">
                      <SvgAF img={IconLoader.icon_warning_round} scaleImg={true} />
                    </div>
                    <div className="viewLogModal_truncatedWarning_text">
                      {
                        'The device has provided a subset of the available logs because the total size of logs is too large.'
                      }
                    </div>
                  </div>
                )}
                {/* <div 
                          className={"viewCurrentLogsButton"} 
                          onClick={() => {
                            dispatch(updateSinglePageDisplayLiveLogs(true));
                            
                            if (displayLiveLogs || !historicalEventLogs) {
                              dispatch(storeLiveEventLogs(deviceId, []))
                              !liveFetching && dispatch(doFetchLiveEventLogs(
                                deviceId,
                                0,
                                10,
                                "timestamp",
                                "desc",
                                (result) => {
                                  dispatch(storeLiveEventLogs(deviceId, result));
                                  let events = result ? result : []
                                  dispatch(updateSinglePageLocalLogList(events))
                                },
                                (error) => {console.log("doFetchLiveEventLogserror", error)},
                                )
                              )
                            } else {
                              dispatch(updateSinglePageLocalLogList(liveEventLogs))
                            }
                          }}>
                          <IconAndText 
                            img={IconLoader.icon_log_start} 
                            hoverImg={IconLoader.icon_log_start} 
                            text={`${liveFetching ? "Retrieving" : "Retrieve"} current logs`}
                          />
                        </div> */}
              </div>
            </div>
          </div>

          <div className="viewLogModal_Content">
            <div className="viewLogModal_ContentChartSection" key={selectedEventLogIndex}>
              <LogsTable
                id={deviceId}
                localLogList={localLogList}
                historicalError={historicalError}
                historicalEventLogs={historicalEventLogs}
                createSnapshotTimeoutMsg={createSnapshotTimeoutMsg}
                allHistoricalEventLogs={allHistoricalEventLogs}
                historicalFetching={historicalFetching}
                liveError={liveError}
                liveEventLogs={liveEventLogs}
                liveFetching={liveFetching}
                selectedEventLogIndex={selectedEventLogIndex}
                selectedEventLogIndexObj={selectedEventLogIndexObj}
                displayLiveLogs={displayLiveLogs}
                timestampOptions={timestampOptions}
                hasCmEventLogsPermission={hasCmEventLogsPermission}
                retrieveEventLogErrorCode={retrieveEventLogErrorCode}
              />
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default memo(ViewLogModal);
